import { Box, Divider, Grid2, Stack, Typography } from "@mui/material";
import {
  useUpdateZyusetuValueByApproveStepMutation,
  useUpdateZyusetuValueMutation,
  useVerifyZyusetuValueMutation,
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyType,
  ZyusetuFieldSectionFragmentFragment,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
} from "@/graphql/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { ZyusetuFieldWrapper } from "@/components/pages/ZyusetuFields/ZyusetuFieldWrapper.tsx";
import { useGetFirstContent, zyusetuValueIntoZodValue, } from "@/hooks/useGetFirstContent";
import { LoadingButton } from "@/components/input/LoadingButton.tsx";
import { Dayjs } from "dayjs";
import { CrossSectionFieldsIds, useSetCrossSectionFields, } from "@/hooks/use-cross-section-fields";
import { FieldValueType } from "@/graphql/config";
import { useZyusetuFieldValueInput } from "@/components/pages/Zyusetu/useZyusetuFieldValueInput.tsx";
import { ZyusetuEditType } from "@/types/ZyusetuEditType.ts";
import { useAtom } from "jotai";
import { ZyusetuAtom } from "@/plugin/jotai-wrapper.ts";

interface ZyusetuEditorSectionProps {
  section: ZyusetuFieldSectionFragmentFragment;
  zyusetuId: string;
  onFocus: (
    value?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment | null,
  ) => void;
  editType: ZyusetuEditType
  onCloseAccordion: () => void;
  approvalStepExecutionId?: string;
}

const fieldState = z.object({
  isReviewed: z.boolean().default(false),
})

const resolver = z
  .discriminatedUnion("keyType", [
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.Boolean),
      value: z.boolean().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.Number),
      value: z.number().nullish(),
      payload: z
        .object({
          thousandSeparator: z.boolean(),
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.Select),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
          items: z.array(z.string()).nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.SelectMany),
      value: z.array(z.string()).nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
          items: z.array(z.string()).nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    // SelectManyWithCustomInput のスキーマ定義を追加 (SelectMany と同じ構造)
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.SelectManyWithCustomInput),
      value: z.array(z.string()).nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
          items: z.array(z.string()).nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.String),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.StringArray),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.StringAutoComplete),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
          items: z.array(z.string()).nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.Date),
      value: z.custom<Dayjs>().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.DateMonth),
      value: z.custom<Dayjs>().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.AutoCompleteManagementCompany),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.AutoCompleteBankAccount),
      value: z.string().nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.Tenant),
      value: z.array(z.object({
        tenant_age: z.string(),
        tenant_name: z.string().min(1),
      })).nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
    z.object({
      keyType: z.literal(ZyusetuFieldKeyType.PremisesLandRights),
      value: z.array(z.object({
        index: z.number().min(1),
        address: z.string().min(1),
        chiban: z.string().min(1),
        chimoku: z.string().min(1),
        chiseki: z.string().min(1),
        rightType: z.string().min(1),
        rightsRatio: z.string().min(1),
      })).nullish(),
      payload: z
        .object({
          availableIf: z
            .array(
              z.object({
                keyName: z.string(),
                value: z.any(),
              }),
            )
            .nullish(),
        })
        .nullish(),
      isRequired: z.boolean(),
    }).merge(fieldState),
  ])
  .refine(
    ({ isRequired, value, payload }) => {
      if (isRequired && !payload?.availableIf) {
        if (value === null || value === undefined || value === "") {
          return false;
        }
      }
      return true;
    },
    {
      message: "必須のフィールドです",
      path: ["value"],
    },
  )
  .refine(
    ({ value, payload, keyType }) => {
      if (keyType === ZyusetuFieldKeyType.Select) {
        if (value && payload?.items) {
          if (!payload.items.includes(value)) {
            return false;
          }
        }
      }
      return true;
    },
    {
      message: "選択肢から選択してください",
      path: ["value"],
    },
  );

const formSchema = z.record(resolver).superRefine((records, ctx) => {
  for (const key in records) {
    if (records[key].payload?.availableIf) {
      if (
        records[key].payload.availableIf?.every(
          (condition) => {
            const currentValue = records[condition.keyName].value
            if (Array.isArray(condition.value)) {
              return condition.value.includes(currentValue);
            }
            return currentValue === condition.value;
          }
        )
      ) {
        if (!records[key].isRequired) {
          continue;
        }
        if (
          records[key].value === null ||
          records[key].value === undefined ||
          records[key].value === ""
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "必須のフィールドです",
            path: [key, "value"],
            fatal: true,
          });
        }
      }
    }
  }
});

const getDefaultvalue = (key: ZyusetuFieldKeyFragmentFragment) => {
  switch (key.keyType) {
    case ZyusetuFieldKeyType.String:
    case ZyusetuFieldKeyType.StringAutoComplete:
      return '';
    case ZyusetuFieldKeyType.SelectMany:
      return [];
    case ZyusetuFieldKeyType.SelectManyWithCustomInput:
      return [];
    case ZyusetuFieldKeyType.Number:
      return null;
    case ZyusetuFieldKeyType.Boolean:
      return false;
    case ZyusetuFieldKeyType.Date:
    case ZyusetuFieldKeyType.DateMonth:
      return null;
    case ZyusetuFieldKeyType.Select:
      return null;
  }
};

export type InputSchema = z.input<typeof formSchema>;

export type FormSchema<
  T extends InputSchema[string]["keyType"] = InputSchema[string]["keyType"],
  U extends InputSchema[string] = InputSchema[string],
> = Record<string, Extract<U, { keyType: T }>>;

export interface UpdateFieldOptions {
  hideToast?: boolean;
  valueMap?: Map<string, FieldValueType>;
  payloadId?: string | undefined;
  templateDescription?: string | null;
}

export function ZyusetuEditorSection({
  section,
  onFocus,
  zyusetuId,
  onCloseAccordion,
  editType,
  approvalStepExecutionId
}: ZyusetuEditorSectionProps) {
  const getFirstContent = useGetFirstContent();

  const setCrossFields = useSetCrossSectionFields();
  const getInitialValue = useCallback(
    (key: ZyusetuFieldKeyFragmentFragment): FormSchema[string] => {
      const isValueNull =
        key.value?.value === null || key.value?.value === undefined;
      const firstContent = getFirstContent(key.analyzedPayloads);

      const myReviewedValue = key.changedValues?.find((changed) => changed.approvalStepExecutionId == approvalStepExecutionId)

      return zyusetuValueIntoZodValue(
        key,
        myReviewedValue?.value ?? (isValueNull
          ? firstContent
            ? firstContent
            : key.defaultValue?.[0]?.value ?? key.keyStyle.defaultValue ?? getDefaultvalue(key)
          : key.value?.value),
      )
    },
    [approvalStepExecutionId, getFirstContent],
  );

  const initialValue = useMemo(() => {
    return section.keys.reduce<FormSchema>((acc, cur) => {
      return {
        ...acc,
        [cur.keyName]: getInitialValue(cur),
      };
    }, {});
  }, [getInitialValue, section]);

  const form = useForm<FormSchema>({
    values: initialValue,
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const updateValue = useUpdateZyusetuValueMutation();
  const updateApproveValue = useUpdateZyusetuValueByApproveStepMutation()
  const updateField = useCallback(
    async (
      keyId: string,
      keyName: string,
      value: FormSchema[string],
      options?: UpdateFieldOptions,
    ) => {
      if (editType === ZyusetuEditType.ReadOnly) {
        toast.error("閲覧モードでは更新できません")
        return;
      }
      if (value.value === null || value.value === undefined) {
        console.error("value is null or undefined", keyName, value);
        return;
      }
      if (typeof value.value === "number") {
        if (isNaN(value.value)) {
          return;
        }
      }
      // if (!form.getFieldState(keyName).isDirty) return

      let loading: string | undefined;
      if (!options?.hideToast) {
        loading = toast.loading("更新中...");
      }

      async function beforeUpdateValue() {
        if (!options?.hideToast)
          toast.success("更新しました", { id: loading });
        form.resetField(keyName, {
          defaultValue: { ...value, isReviewed: editType === ZyusetuEditType.Review },
        });

        if (CrossSectionFieldsIds.includes(keyName))
          setCrossFields((prev) => ({ ...prev, [keyName]: value.value }));

        const related = section.keys.filter((key) => key.keyStyle.calculatorRelatedKeys?.includes(keyName));
        if (related) {
          const valueMap = section.keys.reduce((acc, cur) => {
            acc.set(cur.keyName, cur.keyName === keyName ? value.value : form.getValues(cur.keyName).value);
            return acc;
          }, new Map<string, FieldValueType>())

          try {
            for (const key of related) {
              if (key.keyStyle.calculator) {
                const wrap = () => `{
                  return ${key.keyStyle.calculator};
                }`; //return the block having function expression
                const func = new Function(wrap());
                const props = func
                  .call(null)
                  .call(null, valueMap);

                valueMap.set(key.keyName, zyusetuValueIntoZodValue(key, props).value);

                await updateField(
                  key.id,
                  key.keyName,
                  zyusetuValueIntoZodValue(key, props),
                  {
                    hideToast: true,
                    valueMap
                  }
                );
              }
            }
          } catch (e) {
            console.error(e);
          }
        }
      }

      switch (editType) {
        case ZyusetuEditType.Edit:
          await updateValue
            .mutateAsync({
              zyusetuId: zyusetuId,
              fieldId: keyId,
              value: value.value,
              payloadId: options?.payloadId,
              templateDescription: options?.templateDescription
            }).then(
              () => beforeUpdateValue()
            ).catch(
              () => toast.error("更新に失敗しました", { id: loading })
            )
          break;
        case ZyusetuEditType.Review:
          await updateApproveValue
            .mutateAsync({
              zyusetuId: zyusetuId,
              keyId: keyId,
              value: value.value,
              payloadId: options?.payloadId,
              templateDescription: options?.templateDescription,
              stepExecutionId: approvalStepExecutionId!
            }).then(
              () => beforeUpdateValue()
            ).catch(
              () => toast.error("更新に失敗しました", { id: loading })
            )
          break;
      }
    },
    [approvalStepExecutionId, editType, form, section.keys, setCrossFields, updateApproveValue, updateValue, zyusetuId],
  );

  useEffect(() => {
    for (const key in initialValue) {
      if (CrossSectionFieldsIds.includes(key))
        setCrossFields((prev) => ({
          ...prev,
          [key]: initialValue[key].value,
        }));
    }
  }, [initialValue, setCrossFields])

  const getFieldInput = useZyusetuFieldValueInput(onFocus, updateField, editType);

  const verify = useVerifyZyusetuValueMutation();

  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  const isFirstInRow = useCallback((index: number, items: any[]): boolean => {
    if (index === 0) return true;

    let currentWidth = 0;
    for (let i = 0; i <= index; i++) {
      const itemWidth = items[i].keyStyle.grid ?? 12;

      // 現在のアイテムを追加した場合に12を超えるかチェック
      if (i === index && currentWidth + itemWidth > 12) {
        return true;
      }

      // 現在の行の幅を更新
      currentWidth += itemWidth;

      // 12を超えた場合、新しい行として扱う
      if (currentWidth > 12) {
        currentWidth = itemWidth;
        // 現在処理中のインデックスが、新しい行の開始である場合
        if (i === index) {
          return true;
        }
      }
    }
    return false;
  }, []);

  return (
    <Stack spacing={2} sx={editType === ZyusetuEditType.ReadOnly ? {
      '& .MuiInputBase-root': {
        backgroundColor: 'action.disabledBackground',
      }
    } : undefined}>
      {editType === ZyusetuEditType.ReadOnly && (
        <Typography color="warning.main" variant="subtitle2">
          現在閲覧モードです。フィールドの編集は反映されません。
        </Typography>
      )}
      {section.description && <Typography mb={3} color={'gray'}>{section.description}</Typography>}
      <FormProvider {...form}>
        <Grid2 spacing={1} rowSpacing={2} container={true}>
          {[...section.keys ?? []]
            .sort((a, b) => {
              if (a.keyStyle.index === b.keyStyle.index) {
                return 0;
              }
              if (a.keyStyle.index > b.keyStyle.index) {
                return 1;
              }
              return -1;
            })
            .filter((key) => key.keyStyle.grid !== 0)
            .filter((key) => key.keyStyle.isHidden !== true)
            .map((key, index, arrary) => {
              const isNotHidden = !key.keyStyle.isHidden;
              const isAvailable =
                key.keyStyle.availableIf?.every(
                  (condition) => {
                    const currentValue = form.watch(condition.keyName)?.value
                    if (Array.isArray(condition.value)) {
                      // @ts-expect-error Ojbectとかどうしようもなさそう
                      return condition.value.includes(currentValue);
                    }
                    return currentValue === condition.value;
                  }
                ) ?? true;

              return (
                <Grid2
                  size={key.keyStyle.grid ?? 12}
                  key={`section-${section.id}-key-${key.id}`}
                >
                  {key.keyStyle.isSpace ? (
                    <Box>
                      <Divider sx={{my: 3, mt: 1}}/>
                      {key.description && <Typography>
                        {key.description}
                      </Typography>}
                    </Box>
                  ) : (
                    isNotHidden && (
                      <Box
                        sx={{
                          opacity: isAvailable ? 1 : 0.4,
                          pointerEvents: isAvailable ? "auto" : "none",
                        }}
                      >
                        <ZyusetuFieldWrapper
                          isFirstRow={isFirstInRow(index, arrary)}
                          updateField={updateField}
                          fieldKey={key}
                          editType={editType}
                          onFieldFocus={onFocus}
                          zyusetuId={zyusetuId}
                        >
                          {getFieldInput(key, isAvailable)}
                        </ZyusetuFieldWrapper>
                      </Box>
                    )
                  )}
                </Grid2>
              );
            })}
        </Grid2>
        <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
          <LoadingButton
            props={{
              fullWidth: false,
            }}
            disabled={editType !== ZyusetuEditType.Edit}
            onClick={async () => {
              const trigger = await form.trigger(undefined, {
                shouldFocus: true,
              });
              if (!trigger) {
                toast.error("入力内容に誤りがあります");
                return;
              }
              const loading = toast.loading("確認中...");
              const verified = await verify.mutateAsync(
                {
                  zyusetuId,
                  input: section.keys.map((key) => {
                    return {
                      keyId: key.id,
                      value: form.getValues(key.keyName).value,
                      // FIXME: 選択したpayloadIdを取得する
                      payloadId: "",
                    };
                  }),
                },
              );
              if (verified.verifyZyusetuValue) {
                toast.success("確認しました", { id: loading });
                setZyusetu(verified.verifyZyusetuValue)
                onCloseAccordion();
              } else {
                toast.error("確認に失敗しました", { id: loading });
              }
            }}
          >
            確認済みとしてマークする
          </LoadingButton>
        </Box>
      </FormProvider>
    </Stack>
  );
}
